import { useMemo, useRef } from "react";
import type { RenderUser } from "@/lib/render-user";
import { RENDER_DEBUG } from "@/lib/debug-flags";
import { useStableSet } from "@/lib/use-stable-set";

interface ChannelLike {
  type?: string | null;
  name?: string | null;
}

interface ChannelMember {
  user_id: string;
}

interface UseDmDerivationsArgs {
  channel: ChannelLike | null | undefined;
  user: { id: string } | null | undefined;
  channelMemberList: readonly ChannelMember[];
  renderUserMap: Map<string, RenderUser>;
}

interface UseDmDerivationsResult {
  isDM: boolean;
  dmDisplayName: string | null;
  dmHeaderAvatarUrl: string | null;
  dmHeaderOtherUserId: string | null;
  isCoworkerDm: boolean;
  /** The coworker user_id when this is a coworker DM, otherwise null.
   *  Passed to MessageInput so it can open an SSE stream against
   *  /api/agent/chat for the coworker reply. */
  coworkerDmUserId: string | null;
  /** Set of all coworker user IDs in renderUserMap — used to detect
   *  @mentions of coworkers in non-DM channels. */
  coworkerIds: Set<string>;
}

/**
 * The DM-specific display derivations: name, avatar, "the other user"
 * id, plus whether this is a coworker DM. Pulled out of the parent so
 * the dense channel-context block stays readable.
 */
export function useDmDerivations({
  channel,
  user,
  channelMemberList,
  renderUserMap,
}: UseDmDerivationsArgs): UseDmDerivationsResult {
  const isDM = channel?.type === "dm" || channel?.type === "group_dm";

  const dmDisplayName = useMemo(() => {
    if (!isDM || !user) return null;
    const otherMemberIds = new Set(
      channelMemberList.map((m) => m.user_id).filter((id) => id !== user.id),
    );
    const otherUsers = [...otherMemberIds]
      .map((id) => renderUserMap.get(id))
      .filter((u): u is NonNullable<typeof u> => Boolean(u));
    if (channel?.type === "dm") {
      return otherUsers[0]?.display_name || channel?.name || "User";
    }
    return (
      otherUsers.map((u) => u.display_name).join(", ") ||
      channel?.name ||
      "Group"
    );
  }, [
    channel?.name,
    channel?.type,
    channelMemberList,
    isDM,
    renderUserMap,
    user,
  ]);

  const dmHeaderAvatarUrl = useMemo(() => {
    if (!isDM || !user || channel?.type !== "dm") return null;
    const other = channelMemberList.find((m) => m.user_id !== user.id);
    if (!other) return null;
    return renderUserMap.get(other.user_id)?.avatar_url ?? null;
  }, [isDM, user, channel?.type, channelMemberList, renderUserMap]);

  const dmHeaderOtherUserId = useMemo(() => {
    if (!isDM || !user || channel?.type !== "dm") return null;
    const other = channelMemberList.find((m) => m.user_id !== user.id);
    return other?.user_id ?? null;
  }, [isDM, user, channel?.type, channelMemberList]);

  const isCoworkerDm = useMemo(() => {
    if (!isDM || !user || channel?.type !== "dm") return false;
    const other = channelMemberList.find((m) => m.user_id !== user.id);
    if (!other) return false;
    return renderUserMap.get(other.user_id)?.is_coworker === true;
  }, [isDM, user, channel?.type, channelMemberList, renderUserMap]);

  const coworkerDmUserId = useMemo(() => {
    if (!isCoworkerDm || !user) return null;
    const other = channelMemberList.find((m) => m.user_id !== user.id);
    return other?.user_id ?? null;
  }, [isCoworkerDm, user, channelMemberList]);

  // `coworkerIds` is rebuilt whenever `renderUserMap` flips reference.
  // Even with `useStableMap` upstream, a real presence change on ANY
  // user (online → offline, typing flag, …) bumps the renderUserMap
  // ref, re-running this useMemo and producing a fresh Set with
  // identical content. That fresh ref then flows into
  // `useMessageSend`'s deps → `sendMessage` re-creates → MessageInput's
  // `onSend={sendMessage}` flips → TiptapEditor memo busts.
  //
  // `useStableSet` closes the loop: when the rebuilt Set has the same
  // content as the previous one, return the previous reference.
  // Downstream consumers see a stable identity unless a coworker
  // actually was added/removed.
  /* eslint-disable react-hooks/refs -- diagnostic-only ref read + write
   * gated behind RENDER_DEBUG. Logs whether the underlying memo is
   * rebuilding the Set (useful for verifying the upstream churn we're
   * stabilizing) without affecting render output. */
  const prevRawRef = useRef<Set<string> | null>(null);
  const coworkerIdsRaw = useMemo(() => {
    const ids = new Set<string>();
    for (const [id, u] of renderUserMap) {
      if (u.is_coworker) ids.add(id);
    }
    if (RENDER_DEBUG) {
      const prev = prevRawRef.current;
      const sameSize = prev !== null && prev.size === ids.size;
      let sameContent = sameSize;
      if (sameSize && prev) {
        for (const id of ids) {
          if (!prev.has(id)) {
            sameContent = false;
            break;
          }
        }
      }

      console.debug(
        `[hook:coworkerIds] ${prev === null ? "INIT" : sameContent ? "CHURN-SAME-CONTENT" : "CHANGED"} size=${ids.size}`,
      );
    }
    prevRawRef.current = ids;
    return ids;
  }, [renderUserMap]);
  /* eslint-enable react-hooks/refs */
  const coworkerIds = useStableSet(coworkerIdsRaw);

  return {
    isDM,
    dmDisplayName,
    dmHeaderAvatarUrl,
    dmHeaderOtherUserId,
    isCoworkerDm,
    coworkerDmUserId,
    coworkerIds,
  };
}
